/**
 * Sharing a supporting document, as the client sees it.
 *
 * One control and one list of filenames. What the file *said*, what was read
 * out of it, and what the assessment made of it are all internal: this panel
 * shows the name, the size, and whether it arrived.
 *
 * The file is stored with this assessment on the machine running NS Assistant.
 * Nothing is uploaded to any external service.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchAssessmentDocuments, uploadAssessmentDocument } from '../api/client';
import '../styles/DocumentsPanel.css';

function formatSize(bytes) {
  if (!Number.isFinite(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DocumentUpload({ token, accepted = [], maxBytes = 0 }) {
  const [documents, setDocuments] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const body = await fetchAssessmentDocuments(token);
      setDocuments(body.documents || []);
    } catch {
      // A listing that cannot be read is not worth alarming anybody about
      // mid-conversation; the panel simply shows nothing until it can.
      setDocuments([]);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const onSelect = (event) => {
    setSelected(event.target.files?.[0] || null);
    setError(null);
    setNotice(null);
  };

  const onUpload = async () => {
    if (!selected || busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await uploadAssessmentDocument(token, selected);
      setNotice('Document uploaded successfully.');
      setSelected(null);
      if (inputRef.current) inputRef.current.value = '';
      await load();
    } catch (uploadError) {
      setError(uploadError.message || 'The document could not be uploaded.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="docs" aria-labelledby="client-docs-title">
      <div className="docs__head">
        <h3 id="client-docs-title" className="docs__title">Share a relevant document</h3>
        <p className="docs__subtitle">
          Optional. If you have a process, requirements or sample file that
          would help explain your situation, you can share it here.
        </p>
      </div>

      <div className="docs__picker">
        <label className="docs__choose">
          <input
            ref={inputRef}
            type="file"
            className="docs__input"
            accept={accepted.join(',')}
            onChange={onSelect}
            disabled={busy}
          />
          <span className="docs__choose-label">Choose file</span>
        </label>

        <span className="docs__selected">
          {selected ? `${selected.name} (${formatSize(selected.size)})` : 'No file selected'}
        </span>

        <button
          type="button"
          className="docs__button"
          onClick={onUpload}
          disabled={!selected || busy}
        >
          {busy ? 'Uploading…' : 'Upload'}
        </button>
      </div>

      <p className="docs__hint">
        {accepted.length > 0 && `Accepted: ${accepted.join(' ')}.`}
        {maxBytes > 0 && ` Up to ${formatSize(maxBytes)} per file.`}
      </p>

      {error && <p className="docs__error" role="alert">{error}</p>}
      {notice && <p className="docs__notice" role="status">{notice}</p>}

      {documents.length > 0 && (
        <ul className="docs__list">
          {documents.map((doc) => (
            <li key={doc.filename} className="docs__item">
              <div className="docs__item-main">
                <span className="docs__name">{doc.filename}</span>
                <span className="docs__meta">{formatSize(doc.size_bytes)}</span>
              </div>
              <span className="docs__status docs__status--processed">
                {doc.accepted ? 'Uploaded' : 'Not accepted'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
